import { z } from "zod";
import { tolerateAbsence } from "./synced-row";

/**
 * One warning per unrecognised (enum, value) pair per session: coercions must
 * be visible in the console (and Sentry breadcrumbs) without a widened enum
 * spamming a warn per row.
 */
const warnedUnknownEnumValues = new Set<string>();

/**
 * Tolerant read of one server-authored enum value: a member passes through,
 * anything else warns once and reads as `fallback`. The core both zod
 * wrappers below and hand-written deserialization transforms (see guests.ts)
 * share, so the client's enum-tolerance policy lives in exactly one place.
 */
export function tolerantEnumValue<T extends string, F extends T | null>(
  value: string | null,
  schema: z.ZodType<T>,
  fallback: F
): T | F {
  if (value !== null && schema.safeParse(value).success) {
    return value as T;
  }
  if (value !== null) {
    const { options } = schema as { options?: readonly string[] };
    const key = `${options?.join(",") ?? "enum"}|${value}`;
    if (!warnedUnknownEnumValues.has(key)) {
      warnedUnknownEnumValues.add(key);
      console.warn(
        `[sync] unrecognised server enum value "${value}"` +
          `${options ? ` (this bundle knows: ${options.join(", ")})` : ""}` +
          ` — reading as ${fallback === null ? "null" : `"${fallback}"`}`
      );
    }
  }
  return fallback;
}

/**
 * Reads a server-authored enum column tolerantly, falling back instead of
 * throwing on a value this bundle doesn't recognise.
 *
 * Why every synced enum wants this: a row arriving over PowerSync is validated
 * on the client, and a row that fails validation is left out of its collection
 * (see `_factory.ts`). So a strict enum makes every future server-side value a
 * breaking change for every bundle already installed: the day the server first
 * writes it, older clients stop showing every row that carries it.
 *
 * Widening a DB enum is an expand-phase change, and expand requires old code to
 * tolerate the new shape (docs/migrations.md). This is how the client holds up
 * its half of that bargain.
 *
 * The fallback is deliberately a real member rather than a widening to
 * `string`: keeping the type closed preserves exhaustive `Record<Enum, …>`
 * lookups at every read site, so label maps and switches stay compile-checked.
 * Choose a fallback that is SAFE to mistake the unknown value for — for money,
 * that means the conservative reading (not-yet-settled), never one that would
 * count value the client cannot actually account for.
 */
export function serverEnum<T extends string>(
  schema: z.ZodType<T>,
  fallback: T
) {
  // Input admits `null` because that is what a PowerSync column usually
  // yields: the SQLite view exposes every column in the client schema via
  // `json_extract`, so a key the bucket payload never carried reads back as SQL
  // NULL rather than as an absent key. A bare `z.string()` rejects it before
  // `tolerantEnumValue` (which already answers `null` with the fallback) can
  // run, and every such row drops out of its collection.
  //
  // `tolerateAbsence` answers the other shape a stale row arrives in, the key
  // genuinely missing, so the column reads as the fallback on its own rather
  // than only inside `syncedRow`. It rescues that at runtime and leaves the
  // declared input alone, which is the only way this works: spelling it
  // `.nullish()` makes the field optional in the ZodObject,
  // `powerSyncCollectionOptions` stops inferring the row type from the schema,
  // and EVERY collection degrades to the raw SQLite column types (see
  // `_factory.ts`).
  //
  // Output still narrows to the union, so `Record<T, …>` lookups downstream stay
  // exhaustive. `schema.catch()` would read better but widens the INPUT to
  // `unknown`, which breaks the collection's row inference the same way.
  return tolerateAbsence(
    z
      .string()
      .nullable()
      .transform((value): T => tolerantEnumValue(value, schema, fallback))
  );
}

/**
 * The nullable-column variant: an unrecognised value reads as null — honest
 * "unknown" — instead of masquerading as a real member. Use where every read
 * site handles null: a nullable column, or a row that must be skipped when no
 * member is safe to mistake its value for.
 */
export function serverEnumOrNull<T extends string>(schema: z.ZodType<T>) {
  return tolerateAbsence(
    z
      .string()
      .nullable()
      .transform((value): T | null => tolerantEnumValue(value, schema, null))
  );
}
